import cookie from 'cookie';
import moment from 'moment';

const cookieName = 'butler';

// Cookies written before the path '/' fix are scoped to the directory of whatever route
// was open at the time. The browser sends the most specific path first, so a leftover one
// would shadow the correct cookie. Expire them on the paths we can still address.
const clearLegacyPathCookies = () => {
  const gone = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const segments = window.location.pathname.split('/').filter(part => part.length > 0);

  for (let i = segments.length - 1; i > 0; i--) {
    const legacyPath = '/' + segments.slice(0, i).join('/');
    document.cookie = cookie.serialize(cookieName, '', { path: legacyPath }) + "; " + gone;
  }
};

export const setCookie = (value, ageInSeconds = 7776000) => {
  const d = new Date();
  d.setTime(d.getTime() + ageInSeconds * 1000);
  const expires = "expires=" + d.toUTCString();

  clearLegacyPathCookies();

  // path '/' is required: without it the browser scopes the cookie to the directory of
  // whatever route was open when the token was refreshed, and the engine would not see it
  // on /engine/* snapshot requests.
  document.cookie = cookie.serialize(cookieName, value, { path: '/' }) + "; " + expires;
};

export const getCookie = () => {
  const cookies = cookie.parse(document.cookie);
  if (cookies && cookies[cookieName]) {
    return cookies[cookieName];
  }

  return null;
};

export const isValidToken = (tokenObj) => {
  if (!tokenObj || !tokenObj.token || !tokenObj.expires) {
    return false;
  }

  return parseInt(tokenObj.expires) > Math.floor(new Date().getTime() / 1000) + 300;
};

export const getUserAgentFingerprint = () => {
  return window.navigator.userAgent.replace(/\D+/g, '') + '' + window.navigator.plugins.length + '' + window.navigator.mimeTypes.length;
};

export const sortByPropertyName = (inputArr, propName, order = 1) => {
  let len = inputArr.length;
  let swapped;
  do {
    swapped = false;
    for (let i =0;i<len - 1;i++) {
      const left = inputArr[i];
      const right = inputArr[i+1];
      if (typeof left[propName] !== 'undefined' && typeof right[propName] !== 'undefined') {
        if (parseInt(order) === 1) {
          if (left[propName] > right[propName]) {
            let tmp = inputArr[i];
            inputArr[i] = inputArr[i+1];
            inputArr[i+1] = tmp;
            swapped = true;
          }
        } else if (parseInt(order) === -1) {
          if (left[propName] < right[propName]) {
            let tmp = inputArr[i];
            inputArr[i] = inputArr[i+1];
            inputArr[i+1] = tmp;
            swapped = true;
          }
        }
      }
    }
  } while (swapped);

  return inputArr;
};

export const delay = (ms) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
};

export const inArray = (needle, haystack) => {
  if (typeof needle === 'undefined' || typeof haystack === 'undefined') {
    return false;
  }

  const filtered = haystack.filter(item => {
    return item.toString() === needle.toString();
  });

  return filtered && filtered.length > 0;
};

export const beautifyObjectName = (phrase) => {
  return phrase
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatUptime = (seconds) => {
  if (seconds < 60) {
    return seconds + 's';
  }

  const arr = [];
  const days = Math.floor(seconds / 86400);
  let rest = seconds - days * 86400;
  if (days > 0) {
    arr.push(days + 'd');
  }
  const hours = Math.floor(rest / 3600);
  rest = rest - hours * 3600;
  if (hours > 0) {
    arr.push(hours + 'h');
  }
  const minutes = Math.floor(rest / 60);
  rest = rest - minutes * 60;
  if (minutes > 0) {
    arr.push(minutes + 'm');
  }

  return arr.join(' ');
};

export const formatTime = (timestamp, opt_format) => {
  let format = typeof opt_format !== "undefined" ? opt_format : 'hft';
  const momentTime = moment.unix(timestamp);
  if (format.startsWith('hft')) {
    const today = moment().format('YYYY-MM-DD');
    if (momentTime.format('YYYY-MM-DD') === today) {
      if (format === 'hft') {
        return momentTime.format('HH:mm');
      } else if (format === 'hfts') {
        return momentTime.format('HH:mm:ss');
      }
    }

    if (format === 'hft') {
      return momentTime.format('YYYY-MM-DD HH:mm');
    } else if (format === 'hfts') {
      return momentTime.format('YYYY-MM-DD HH:mm:ss');
    }
  }

  return momentTime.format(format);
};

export const randomInteger = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const ab2str = (buf) => {
  return String.fromCharCode.apply(null, new Uint16Array(buf));
};

export const checkIfMobileUserAgent = () => {
  let check = false;
  /* eslint-disable-next-line */
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
  return check;
};

// Snapshots are served by the engine per camera id, authenticated via the 'butler' cookie.
// nonce is only there to defeat the browser image cache.
// Used by the dashboard tiles: they poll rather than stream, because HTTP/1.1 limits a
// browser to ~6 connections per origin and a stream would hold one open permanently.
export const cameraSnapshotUrl = (cameraId, nonce) => {
  return `/engine/cameras/${cameraId}/snapshot?r=${nonce}`;
};

// A push stream (multipart/x-mixed-replace) the browser renders straight into an <img>.
// One connection, no client timers. Used only where a single camera is on screen.
// attempt changes only to force a fresh connection after a drop.
export const cameraStreamUrl = (cameraId, attempt) => {
  return `/engine/cameras/${cameraId}/stream?c=${attempt}`;
};
